import React, { useState, useRef } from 'react';
import Navbar from "../../layout/Navbar";
import Footer from "../../layout/Footer";
import { Upload, X, CheckCircle, AlertCircle, Plus, Pen, Image as ImageIcon, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

const MedicalCertificateForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenNumber, setTokenNumber] = useState('');
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState('');

  // Signature refs and state
  const sigCanvas = useRef(null);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [signatureDataURL, setSignatureDataURL] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    // Application Date & Payment Info
    applicationDate: getTodayDate(),
    depositAmount: '20', // Hard-coded to 20
    paymentMode: '', // Changed from paymentFor to paymentMode (online/offline)
    // certificateTypes removed completely

    // Applicant Information
    applicantName: '',
    doctorName: '',
    departmentType: '',
    treatmentStartDate: '',
    treatmentEndDate: '',
    treatmentStatus: '',
    uhidNumber: '',

    // Contact Information
    applicantSignature: '',
    applicantAddress: '',
    applicantMobile: '',

    // File Upload
    dischargeOpdSlipFiles: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle multiple file uploads
  const handleMultipleFileChange = (e, fieldName) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const fileErrors = [];

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        fileErrors.push(`${file.name}: File too large (max 5MB)`);
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        fileErrors.push(`${file.name}: Invalid file type`);
        return;
      }

      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors(prev => ({ ...prev, [fieldName]: fileErrors.join(', ') }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [fieldName]: [...prev[fieldName], ...validFiles]
    }));

    setErrors(prev => ({ ...prev, [fieldName]: '' }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, [fieldName]: 'File size should be less than 5MB' }));
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, [fieldName]: 'Only JPG, PNG, and PDF files are allowed' }));
        return;
      }
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const removeFile = (fieldName, index) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  // Signature handling functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setSignatureDataURL(null);
    setSignatureFile(null);
    setErrors(prev => ({ ...prev, applicantSignature: '' }));
  };

  const saveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataURL = sigCanvas.current.toDataURL('image/png');
      setSignatureDataURL(dataURL);
      setErrors(prev => ({ ...prev, applicantSignature: '' }));
    } else {
      setErrors(prev => ({ ...prev, applicantSignature: 'Please draw your signature / कृपया अपना हस्ताक्षर करें' }));
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, applicantSignature: 'File size should be less than 2MB' }));
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, applicantSignature: 'Only JPG and PNG images allowed' }));
        return;
      }

      setSignatureFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureDataURL(reader.result);
        setErrors(prev => ({ ...prev, applicantSignature: '' }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Payment Mode validation - NEW
    if (!formData.paymentMode) newErrors.paymentMode = 'Required / आवश्यक';

    // Required fields validation
    if (!formData.applicantName.trim()) newErrors.applicantName = 'Required / आवश्यक';
    if (!formData.doctorName.trim()) newErrors.doctorName = 'Required / आवश्यक';
    if (!formData.departmentType) newErrors.departmentType = 'Required / आवश्यक';
    if (!formData.treatmentStartDate) newErrors.treatmentStartDate = 'Required / आवश्यक';
    if (!formData.treatmentEndDate) newErrors.treatmentEndDate = 'Required / आवश्यक';
    if (!formData.treatmentStatus) newErrors.treatmentStatus = 'Required / आवश्यक';

    if (!formData.applicantAddress.trim()) newErrors.applicantAddress = 'Required / आवश्यक';
    if (!formData.applicantMobile.trim()) newErrors.applicantMobile = 'Required / आवश्यक';
    else if (!/^\d{10}$/.test(formData.applicantMobile)) {
      newErrors.applicantMobile = 'Invalid Mobile Number / अमान्य मोबाइल नंबर';
    }

    // Signature validation
    if (!signatureDataURL) {
      newErrors.applicantSignature = 'Applicant signature is required / प्रार्थी का हस्ताक्षर आवश्यक है';
    }

    // File validation
    if (formData.dischargeOpdSlipFiles.length === 0) {
      newErrors.dischargeOpdSlipFiles = 'At least one file required / कम से कम एक फ़ाइल आवश्यक';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = document.querySelector('.border-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();

      Object.keys(formData).forEach(key => {
        if (key !== 'dischargeOpdSlipFiles') {
          if (formData[key] !== null && formData[key] !== '') {
            submitData.append(key, formData[key]);
          }
        }
      });

      // Append applicant signature
      if (signatureMode === 'draw' && signatureDataURL) {
        const response = await fetch(signatureDataURL);
        const blob = await response.blob();
        submitData.append('applicantSignature', blob, 'applicant-signature.png');
      } else if (signatureMode === 'upload' && signatureFile) {
        submitData.append('applicantSignature', signatureFile);
      }

      // Append multiple files
      formData.dischargeOpdSlipFiles?.forEach(file => {
        submitData.append('dischargeOpdSlipFiles', file);
      });

      const response = await fetch('https://wellnessplix.in/formProxy.php?endpoint=medical-certificate', {
        method: 'POST',
        body: submitData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit form');
      }

      if (result.success) {
        setTokenNumber(result.data.submissionNo);
        setPdfUrl(result.data.pdfUrl);
        setSuccess(true);
      } else {
        throw new Error(result.message || 'Submission failed');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: error.message || 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      applicationDate: getTodayDate(),
      depositAmount: '100', // Hard-coded to 100
      paymentMode: '',
      applicantName: '',
      doctorName: '',
      departmentType: '',
      treatmentStartDate: '',
      treatmentEndDate: '',
      treatmentStatus: '',
      uhidNumber: '',
      applicantSignature: '',
      applicantAddress: '',
      applicantMobile: '',
      dischargeOpdSlipFiles: []
    });
    clearSignature();
    setSuccess(false);
    setTokenNumber('');
    setErrors({});
  };

  if (success) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="flex justify-center mb-6">
                <CheckCircle className="w-20 h-20 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                सफलतापूर्वक जमा किया गया!<br />
                Successfully Submitted!
              </h2>
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <p className="text-gray-600 mb-2">
                  आपका टोकन नंबर / Your Token Number
                </p>
                <p className="text-4xl font-bold text-blue-600 mb-4">
                  {tokenNumber}
                </p>
                <p className="text-sm text-gray-500">
                  यह नंबर व्हाट्सएप पर भेज दिया गया है<br />
                  This number has been sent to WhatsApp
                </p>
              </div>

              {/* PDF Download Button */}
              {pdfUrl && (
                <div className="mb-6">
                  <a
                    href={`http://3.6.79.46:8000${pdfUrl}`}
                    download
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors shadow-lg hover:shadow-xl"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download PDF / पीडीएफ डाउनलोड करें
                  </a>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={resetForm}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  नया आवेदन भरें / Fill New Application
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  होम पर जाएं / Go to Home
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="text-center mb-4">
              <p className="text-lg font-semibold text-gray-700 mb-2">📝 आवेदन प्रारूप</p>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                चिकित्सा प्रमाण पत्र
              </h1>
              <p className="text-xl md:text-2xl text-gray-700">
                Medical Certificate
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            {/* Application Date & Payment Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                आवेदन जानकारी / Application Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Application Date */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    दिनांक / Date
                  </label>
                  <input
                    type="date"
                    name="applicationDate"
                    value={formData.applicationDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Deposit Amount - HARD-CODED TO 100 AND DISABLED */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Deposit Rs. (जमा राशि)
                  </label>
                  <input
                    type="number"
                    name="depositAmount"
                    value={formData.depositAmount}
                    onChange={handleInputChange}
                    disabled={true}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Fixed amount / निश्चित राशि: ₹100
                  </p>
                </div>

                {/* Payment Mode - REQUIRED WITH ASTERISK */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    भुगतान का माध्यम / Payment Mode <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="online"
                        checked={formData.paymentMode === 'online'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">ऑनलाइन / Online</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="offline"
                        checked={formData.paymentMode === 'offline'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">ऑफलाइन / Offline</span>
                    </label>
                  </div>
                  {errors.paymentMode && (
                    <p className="text-red-500 text-sm mt-1">{errors.paymentMode}</p>
                  )}
                </div>

                {/* Certificate Types REMOVED */}
              </div>
            </div>

            {/* Service To Section */}
            <div className="mb-8">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <span className="font-semibold">सेवा में / To:</span>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  आचार्य प्रभारी, ट्रॉमा सेंटर<br />
                  Acharya Prabhari, Trauma Center<br />
                  चिकित्सा विज्ञान संस्थान<br />
                  Institute of Medical Sciences<br />
                  काशी हिन्दू विश्वविद्यालय<br />
                  Banaras Hindu University
                </p>
              </div>
            </div>

            {/* Application Content Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                आवेदन विवरण / Application Details
              </h2>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700 mb-4">
                  <span className="font-semibold">महोदय / Sir,</span>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  निवेदन है कि प्रार्थी/प्रार्थिनी का इलाज आपके चिकित्सालय में डॉ की निगरानी में,
                  बहिरंग/अंतरंग विभाग के अंतर्गत, दिनांक से दिनांक तक चला / चल रहा है।
                </p>
                <p className="text-gray-700 leading-relaxed mt-2 text-sm">
                  <em>Request that the applicant's treatment in your hospital under Dr. (name),
                    in outpatient/inpatient department, from (start date) to (end date) was completed/is ongoing.</em>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Applicant Name */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    प्रार्थी/प्रार्थिनी का नाम / Applicant Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="applicantName"
                    value={formData.applicantName}
                    onChange={handleInputChange}
                    placeholder="पूरा नाम दर्ज करें / Enter full name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.applicantName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.applicantName && (
                    <p className="text-red-500 text-sm mt-1">{errors.applicantName}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    यु.एच.आई.डी. नंबर / UHID Number
                  </label>
                  <input
                    type="text"
                    name="uhidNumber"
                    value={formData.uhidNumber}
                    onChange={handleInputChange}
                    placeholder="यु.एच.आई.डी. नंबर / UHID Number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Doctor Name */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    डॉक्टर का नाम / Doctor Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="doctorName"
                    value={formData.doctorName}
                    onChange={handleInputChange}
                    placeholder="डॉक्टर का नाम / Doctor Name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.doctorName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.doctorName && (
                    <p className="text-red-500 text-sm mt-1">{errors.doctorName}</p>
                  )}
                </div>

                {/* Department Type */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    विभाग / Department <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="departmentType"
                        value="outpatient"
                        checked={formData.departmentType === 'outpatient'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">बहिरंग विभाग / Outpatient (OPD)</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="departmentType"
                        value="inpatient"
                        checked={formData.departmentType === 'inpatient'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">अंतरंग विभाग / Inpatient (IPD)</span>
                    </label>
                  </div>
                  {errors.departmentType && (
                    <p className="text-red-500 text-sm mt-1">{errors.departmentType}</p>
                  )}
                </div>

                {/* Treatment Start Date */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    उपचार प्रारंभ दिनांक / Treatment Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="treatmentStartDate"
                    value={formData.treatmentStartDate}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.treatmentStartDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.treatmentStartDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.treatmentStartDate}</p>
                  )}
                </div>

                {/* Treatment End Date */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    उपचार समाप्ति दिनांक / Treatment End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="treatmentEndDate"
                    value={formData.treatmentEndDate}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.treatmentEndDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.treatmentEndDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.treatmentEndDate}</p>
                  )}
                </div>

                {/* Treatment Status */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    उपचार की स्थिति / Treatment Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="treatmentStatus"
                        value="completed"
                        checked={formData.treatmentStatus === 'completed'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">चला (समाप्त) / Completed</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="treatmentStatus"
                        value="ongoing"
                        checked={formData.treatmentStatus === 'ongoing'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">चल रहा है / Ongoing</span>
                    </label>
                  </div>
                  {errors.treatmentStatus && (
                    <p className="text-red-500 text-sm mt-1">{errors.treatmentStatus}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Request Section */}
            <div className="mb-8">
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-gray-700 leading-relaxed">
                  <span className="font-semibold">अतः</span> श्रीमान जी से निवेदन है कि प्रार्थी/प्रार्थिनी को
                  उपरोक्त से संबंधित चिकित्सा प्रमाण पत्र निर्गत करने की कृपा करें।
                </p>
                <p className="text-gray-600 text-sm mt-2">
                  <em>Therefore, it is requested to issue a medical certificate related to the above to the applicant.</em>
                </p>
              </div>
            </div>

            {/* Applicant Contact Information */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                प्रार्थी की जानकारी / Applicant Information
              </h2>

              <div className="grid grid-cols-1 gap-4">
                {/* Address */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    पता / Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="applicantAddress"
                    value={formData.applicantAddress}
                    onChange={handleInputChange}
                    placeholder="पूरा पता दर्ज करें / Enter full address"
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.applicantAddress ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.applicantAddress && (
                    <p className="text-red-500 text-sm mt-1">{errors.applicantAddress}</p>
                  )}
                </div>

                {/* Mobile Number */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    मोबाइल नंबर / Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="applicantMobile"
                    value={formData.applicantMobile}
                    onChange={handleInputChange}
                    placeholder="10 अंकों का मोबाइल नंबर / 10 digit mobile"
                    maxLength="10"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.applicantMobile ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.applicantMobile && (
                    <p className="text-red-500 text-sm mt-1">{errors.applicantMobile}</p>
                  )}
                </div>
              </div>
            </div>

            {/* File Upload Section - MULTIPLE FILES */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                संलग्नक / Attachment
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                कृपया निम्नलिखित दस्तावेज़ संलग्न करें / Please attach the following document
              </p>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  डिस्चार्ज / ओ.पी.डी स्लिप की छाया प्रति <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  Discharge / OPD Slip Copy | You can upload multiple files / आप कई फ़ाइलें अपलोड कर सकते हैं
                </p>

                {/* Display uploaded files */}
                {formData.dischargeOpdSlipFiles.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.dischargeOpdSlipFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-purple-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded">
                            {index + 1}
                          </span>
                          <span className="text-sm text-gray-700 truncate">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile('dischargeOpdSlipFiles', index)}
                          className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload button */}
                <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${errors.dischargeOpdSlipFiles ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-400'
                  }`}>
                  <label className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      {formData.dischargeOpdSlipFiles.length === 0 ? (
                        <>
                          <Upload className="w-12 h-12 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">
                            फ़ाइल अपलोड करने के लिए क्लिक करें<br />
                            Click to upload file
                          </span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-10 h-10 text-purple-600 mb-2" />
                          <span className="text-sm text-purple-600 font-semibold">
                            और फ़ाइल जोड़ें / Add More Files
                          </span>
                        </>
                      )}
                      <span className="text-xs text-gray-400 mt-2">
                        JPG, PNG, PDF (Max 5MB each) | Multiple files allowed
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      multiple
                      onChange={(e) => handleMultipleFileChange(e, 'dischargeOpdSlipFiles')}
                      className="hidden"
                    />
                  </label>
                </div>
                {errors.dischargeOpdSlipFiles && (
                  <p className="text-red-500 text-sm mt-1">⚠️ {errors.dischargeOpdSlipFiles}</p>
                )}
              </div>
            </div>

            {/* Applicant Signature Section - NEW */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                प्रार्थी/प्रार्थिनी के हस्ताक्षर / Applicant Signature <span className="text-red-500">*</span>
              </h2>

              {/* Mode Selection */}
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setSignatureMode('draw');
                    clearSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${signatureMode === 'draw'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <Pen className="w-5 h-5" />
                  Draw Signature / हस्ताक्षर करें
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSignatureMode('upload');
                    clearSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${signatureMode === 'upload'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <ImageIcon className="w-5 h-5" />
                  Upload Image / फ़ोटो अपलोड करें
                </button>
              </div>

              {/* Draw Mode */}
              {signatureMode === 'draw' && (
                <div>
                  <div className={`border-2 rounded-lg overflow-hidden ${errors.applicantSignature ? 'border-red-500' : 'border-gray-300'
                    }`}>
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: 'w-full h-48 bg-white cursor-crosshair',
                        style: { touchAction: 'none' }
                      }}
                      backgroundColor="white"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="flex-1 py-2 px-4 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Clear / साफ़ करें
                    </button>
                    <button
                      type="button"
                      onClick={saveSignature}
                      className="flex-1 py-2 px-4 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Save / सहेजें
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    👆 Use mouse or finger to draw your signature / अपना हस्ताक्षर बनाने के लिए माउस या उंगली का उपयोग करें
                  </p>
                </div>
              )}

              {/* Upload Mode */}
              {signatureMode === 'upload' && (
                <div>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${errors.applicantSignature ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                    }`}>
                    <label className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          Upload signature image / हस्ताक्षर की फ़ोटो अपलोड करें
                        </span>
                        <span className="text-xs text-gray-400 mt-2">
                          JPG, PNG (Max 2MB)
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleSignatureUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Signature Preview */}
              {signatureDataURL && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700 font-semibold mb-2">
                    Signature Preview / हस्ताक्षर पूर्वावलोकन:
                  </p>
                  <div className="bg-white p-4 rounded border border-gray-300 flex justify-center">
                    <img
                      src={signatureDataURL}
                      alt="Applicant Signature"
                      className="max-h-32 object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Remove / हटाएं
                  </button>
                </div>
              )}

              {errors.applicantSignature && (
                <p className="text-red-500 text-sm mt-2">⚠️ {errors.applicantSignature}</p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-4 px-6 rounded-lg font-semibold text-white transition-all ${loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                  }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    जमा हो रहा है... / Submitting...
                  </span>
                ) : (
                  'जमा करें / Submit'
                )}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 sm:flex-none px-6 py-4 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                रीसेट करें / Reset
              </button>
            </div>

            {/* Footer Note */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 text-center">
                <span className="font-semibold">नोट / Note:</span> सभी आवश्यक फ़ील्ड (<span className='text-red-500'>*</span>) भरना अनिवार्य है।<br />
                All required fields (<span className='text-red-500'>*</span>) must be filled.
              </p>
            </div>

            {/* Signature Section */}
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg text-right">
              <p className="text-gray-700 font-semibold mb-2">प्रार्थी/प्रार्थिनी</p>
              <p className="text-gray-600 text-sm">Applicant</p>
              <div className="mt-4 text-gray-500 text-sm">
                हस्ताक्षर / Signature: _________________
              </div>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MedicalCertificateForm;
